import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, FadeIn } from "@/components/site/Motion";
import { CtaButton, CtaRow, BookMeeting } from "@/components/site/Cta";
import { HeroCanvas } from "@/components/site/HeroCanvas";

const PLATFORMS = ["Google Search", "AI Overviews", "ChatGPT", "Gemini", "Perplexity"];

export const HomeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCanvas = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} data-testid="home-hero" className="grain relative min-h-[100svh] overflow-hidden bg-ink text-white">
      <div aria-hidden="true" className="grid-lines absolute inset-0 [mask-image:radial-gradient(ellipse_at_70%_40%,black,transparent_70%)]" />
      <div aria-hidden="true" className="absolute right-[-15%] top-[5%] h-[720px] w-[720px] rounded-full bg-violet-700/30 blur-[150px]" />
      <div aria-hidden="true" className="absolute bottom-[-25%] left-[-10%] h-[560px] w-[560px] rounded-full bg-indigo-800/30 blur-[140px]" />

      <motion.div style={{ y: yCanvas, scale }} className="pointer-events-none absolute inset-y-0 right-[-30%] w-[130%] opacity-50 sm:right-[-12%] sm:w-[85%] sm:opacity-70 lg:right-[-6%] lg:w-[62%] lg:opacity-100">
        <FadeIn delay={0.4} className="h-full w-full"><HeroCanvas /></FadeIn>
      </motion.div>

      <motion.div style={{ y: yText }} className="wrap relative flex min-h-[100svh] flex-col justify-end pb-14 pt-32 sm:pb-20">
        <FadeIn className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-violet-300">
          <span className="h-2 w-2 animate-pulse rounded-full bg-violet-400" /> Perfect Digitals UK
        </FadeIn>
        <MaskedLines
          testid="hero-title"
          className="mt-7 font-display text-[3rem] font-medium leading-[0.92] sm:text-7xl lg:text-[6.2rem] xl:text-[7.4rem]"
          lines={[
            <>SEO <span className="font-serif-i text-violet-300">&amp;</span> AI Search</>,
            <span className="text-gradient">Visibility</span>,
            <>Specialists<span className="text-violet-400">.</span></>,
          ]}
        />
        <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
          <FadeIn delay={0.7} className="lg:col-span-6">
            <h2 className="font-serif-i text-2xl leading-snug text-violet-100 sm:text-3xl">Get found on Google. Get discovered and recommended by AI.</h2>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-slate-300 sm:text-base">
              Perfect Digitals helps businesses increase their visibility across Google and the rapidly evolving world of AI-powered search.
            </p>
            <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-slate-400 sm:text-base">
              We combine proven SEO with AI Search Optimisation to help your business become easier to find, understand and recommend across Google Search, AI Overviews, ChatGPT, Gemini, Perplexity and other emerging search experiences.
            </p>
          </FadeIn>
          <FadeIn delay={0.85} className="lg:col-span-5 lg:col-start-8">
            <CtaRow className="lg:justify-end">
              <BookMeeting testid="hero-book-meeting-cta" />
              <CtaButton label="Speak to a Search Specialist" variant="ghost" testid="hero-specialist-cta" />
            </CtaRow>
          </FadeIn>
        </div>
        <FadeIn delay={1} className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-white/10 pt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
          <span className="text-slate-300">Visible across</span>
          {PLATFORMS.map((p) => <span key={p} className="transition-colors hover:text-violet-200">{p}</span>)}
        </FadeIn>
      </motion.div>
    </section>
  );
};
